import {
  AnyAction,
  combineReducers,
  configureStore,
  Dispatch,
  isRejected,
  PreloadedState,
} from '@reduxjs/toolkit'
import appStateReducer from './features/appConfigSlice'
import { screensRouterReducer } from './features/screensRouterSlice'
import { treatmentScreenReducer } from './features/treatmentScreen/treatmentScreenSlice'
import { clinicScreenReducer } from './features/clinicScreen/clinicScreenSlice'
import { appointmentScreenReducer } from './features/appointmentScreen/appointmentScreenSlice'
import { bookingFlowReducer } from './features/bookingFlow/bookingFlowSlice'
import cityScreenReducer from './features/cityScreenSlice'
import bookingSummaryScreenReducer from './features/bookingSummaryScreenSlice'
import contactInformationScreenReducer from './features/contactInformationScreenSlice'
import myBookingsSliceReducer from './features/myBookingsSlice'
import yourBookingsSliceReducer from './features/yourBookingsSlice'
import treatmentSelectionSliceReducer from './features/treatmentSelectionSlice'
import { bookingDetailsSliceReducer } from './features/bookingDetails/bookingDetailsSlice'
import * as Sentry from '@sentry/react'

const rootReducer = combineReducers({
  app: appStateReducer,
  screensRouter: screensRouterReducer,
  treatmentScreen: treatmentScreenReducer,
  treatmentSelection: treatmentSelectionSliceReducer,
  cityScreen: cityScreenReducer,
  clinicScreen: clinicScreenReducer,
  appointmentScreen: appointmentScreenReducer,
  bookingDetailsScreen: bookingDetailsSliceReducer,
  bookingSummaryScreen: bookingSummaryScreenReducer,
  contactInformationScreen: contactInformationScreenReducer,
  bookingFlowState: bookingFlowReducer,
  myBookingsScreen: myBookingsSliceReducer,
  yourBookingsScreen: yourBookingsSliceReducer,
})

export type RootState = ReturnType<typeof rootReducer>

export const setupStore = (preloadedState?: PreloadedState<RootState>) =>
  configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }).concat(() => {
        return (next: Dispatch<AnyAction>) => (action: AnyAction) => {
          const result: unknown = next(action)
          const args = action?.meta?.arg
          if (isRejected(result) && !result.meta.rejectedWithValue) {
            const error = new Error(
              result.error.message || 'Something went wrong'
            )
            if (result.error.name) {
              error.name = result.error.name
            }
            if (result.error.stack) {
              error.stack = result.error.stack
            }

            Sentry.captureException(error.message, {
              extra: {
                message: error.message,
                type: action.type,
                args,
              },
            })
          }
          return result
        }
      }),
  })

export const store = setupStore()

export type AppDispatch = typeof store.dispatch
